import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import MapView from "@/components/MapView";
import { num } from "@/lib/format";
import { Crosshair, X, Map as MapIcon, LocateFixed, Eye } from "lucide-react";

const LS = {
  x: "manual_control_minimap_x",
  y: "manual_control_minimap_y",
  w: "manual_control_minimap_width",
  h: "manual_control_minimap_height",
  follow: "manual_control_minimap_follow_drone",
  opacity: "manual_control_minimap_opacity",
};
const MIN_W = 260, MIN_H = 180, MAX_W = 620, MAX_H = 420;
const OPACITIES = [0.5, 0.75, 1];

function lsNum(key, def) {
  const v = parseFloat(localStorage.getItem(key));
  return Number.isFinite(v) ? v : def;
}

function haversineM(a, b) {
  if (!a || !b) return null;
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180, la2 = (b.lat * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

// Transparent, draggable & resizable tactical mini-map overlay (cockpit-integrated).
export default function CockpitMiniMap({ drone, trace, homePoint, configuredBasePoint, onHide, onEvent, cockpitRef }) {
  const [size, setSize] = useState(() => ({ w: lsNum(LS.w, 320), h: lsNum(LS.h, 220) }));
  const [pos, setPos] = useState(() => ({ x: lsNum(LS.x, NaN), y: lsNum(LS.y, 88) }));
  const [follow, setFollow] = useState(() => localStorage.getItem(LS.follow) === "1");
  const [opacity, setOpacity] = useState(() => lsNum(LS.opacity, 0.85));
  const [recenterTarget, setRecenterTarget] = useState(null);

  const posRef = useRef(pos), sizeRef = useRef(size), droneRef = useRef(drone);
  posRef.current = pos; sizeRef.current = size; droneRef.current = drone;

  // UNIFIED.6 — distance au PX4 HOME (référence RTL autoritaire). Aucune ETA fabriquée : la vitesse
  // RTL réelle n'est pas connue, on n'affiche donc PLUS `dist/8`. HOME inconnu => "—" (jamais la base).
  const homeDist = useMemo(
    () => (homePoint && Number.isFinite(homePoint.lat) ? haversineM(drone, { lat: homePoint.lat, lng: homePoint.lng }) : null),
    [drone?.lat, drone?.lng, homePoint] // eslint-disable-line
  );

  // Initialise X to top-right inside cockpit when not yet stored.
  useEffect(() => {
    if (Number.isNaN(posRef.current.x)) {
      const cw = cockpitRef?.current?.clientWidth || 900;
      setPos((p) => ({ ...p, x: Math.max(0, cw - sizeRef.current.w - 24) }));
    }
  }, [cockpitRef]);

  // Persist
  useEffect(() => { if (!Number.isNaN(pos.x)) { localStorage.setItem(LS.x, String(Math.round(pos.x))); localStorage.setItem(LS.y, String(Math.round(pos.y))); } }, [pos]);
  useEffect(() => { localStorage.setItem(LS.w, String(Math.round(size.w))); localStorage.setItem(LS.h, String(Math.round(size.h))); }, [size]);
  useEffect(() => { localStorage.setItem(LS.follow, follow ? "1" : "0"); }, [follow]);
  useEffect(() => { localStorage.setItem(LS.opacity, String(opacity)); }, [opacity]);

  // MAP.NAV.1 — SUIVRE = mode de navigation du moteur cartographique commun (drone centré, avant vers
  // le haut, fond tournant, repère NORD). Plus aucun timer de recentrage : le recentrage suit les
  // changements de position. Un déplacement manuel de la carte désactive SUIVRE (nord en haut).
  const onFollowChange = useCallback((v) => { setFollow(v); if (!v) onEvent?.("minimap_follow_off_manual_pan"); }, [onEvent]);

  const clampPos = (x, y) => {
    const cw = cockpitRef?.current?.clientWidth || 9999, ch = cockpitRef?.current?.clientHeight || 9999;
    return { x: Math.max(0, Math.min(x, cw - sizeRef.current.w)), y: Math.max(0, Math.min(y, ch - sizeRef.current.h)) };
  };

  const startDrag = useCallback((e) => {
    if (e.target.closest("button")) return;
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY, ox = posRef.current.x, oy = posRef.current.y;
    const move = (ev) => setPos(clampPos(ox + (ev.clientX - sx), oy + (ev.clientY - sy)));
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); onEvent?.("minimap_drag"); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  }, []); // eslint-disable-line

  const startResize = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sy = e.clientY, ow = sizeRef.current.w, oh = sizeRef.current.h;
    const cw = cockpitRef?.current?.clientWidth || 9999, ch = cockpitRef?.current?.clientHeight || 9999;
    const move = (ev) => {
      const maxW = Math.min(MAX_W, cw - posRef.current.x);
      const maxH = Math.min(MAX_H, ch - posRef.current.y);
      setSize({
        w: Math.max(MIN_W, Math.min(ow + (ev.clientX - sx), maxW)),
        h: Math.max(MIN_H, Math.min(oh + (ev.clientY - sy), maxH)),
      });
    };
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); onEvent?.("minimap_resize"); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  }, []); // eslint-disable-line

  const toggleFollow = () => { const nf = !follow; setFollow(nf); onEvent?.(nf ? "minimap_follow_on" : "minimap_follow_off"); };
  const cycleOpacity = () => { const i = OPACITIES.findIndex((v) => Math.abs(v - opacity) < 0.02); const nv = OPACITIES[(i + 1) % OPACITIES.length]; setOpacity(nv); onEvent?.("minimap_opacity_change"); };
  const recenter = () => { const d = droneRef.current; if (d?.lat != null) setRecenterTarget([d.lat, d.lng]); onEvent?.("minimap_recenter"); };

  const left = Number.isNaN(pos.x) ? undefined : pos.x;

  return (
    <div
      data-testid="cockpit-minimap"
      className="minimap-leaflet pointer-events-auto absolute flex flex-col overflow-hidden"
      style={{
        left, top: pos.y, width: size.w, height: size.h,
        maxWidth: "calc(100% - 16px)", maxHeight: "calc(100% - 96px)", zIndex: 40, opacity,
        background: "rgba(3, 10, 18, 0.35)",
        border: "1px solid rgba(34, 211, 238, 0.35)",
        backdropFilter: "blur(4px)",
        boxShadow: "0 0 18px rgba(34, 211, 238, 0.18)",
        borderRadius: 10,
      }}
    >
      {/* Minimal draggable header */}
      <div data-testid="minimap-header" onMouseDown={startDrag}
        className="shrink-0 flex items-center gap-1.5 px-2 py-1 cursor-move select-none"
        style={{ background: "rgba(34,211,238,0.06)" }}>
        <MapIcon size={11} className="text-[#22D3EE]" />
        <span className="font-mono text-[8px] tracking-[0.2em] uppercase text-[#22D3EE]/90">Mini-carte</span>
        <div className="ml-auto flex items-center gap-0.5">
          <button data-testid="minimap-follow" onClick={toggleFollow} title="Suivre le drone"
            className={`flex items-center gap-1 px-1.5 h-5 rounded-sm font-mono text-[8px] uppercase tracking-wider transition-colors ${follow ? "bg-[#22D3EE]/25 text-[#22D3EE]" : "text-white/60 hover:bg-white/10"}`}>
            <LocateFixed size={11} /> Suivre
          </button>
          <button data-testid="minimap-opacity" onClick={cycleOpacity} title="Opacité"
            className="flex items-center gap-0.5 px-1 h-5 rounded-sm font-mono text-[8px] text-white/60 hover:bg-white/10">
            <Eye size={11} /> {Math.round(opacity * 100)}%
          </button>
          <button data-testid="minimap-recenter" onClick={recenter} title="Recentrer"
            className="w-5 h-5 flex items-center justify-center rounded-sm text-[#22D3EE]/80 hover:bg-[#22D3EE]/15"><Crosshair size={12} /></button>
          <button data-testid="minimap-hide" onClick={onHide} title="Masquer"
            className="w-5 h-5 flex items-center justify-center rounded-sm text-white/70 hover:bg-white/10"><X size={12} /></button>
        </div>
      </div>

      {/* Map */}
      <div className="relative flex-1">
        <MapView
          drones={[drone]}
          realPath={trace}
          basePoint={configuredBasePoint}
          homePoint={homePoint}
          recenter={recenterTarget}
          followId={follow && drone ? drone.drone_id : null}
          onFollowChange={onFollowChange}
          homeLink={homePoint && Number.isFinite(homePoint.lat) && drone?.lat != null ? [[drone.lat, drone.lng], [homePoint.lat, homePoint.lng]] : null}
        />
        <div data-testid="minimap-rtl-info" className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center justify-between font-mono text-[9px] bg-black/45 border border-[#22D3EE]/25 rounded-sm px-2 py-1 backdrop-blur-sm pointer-events-none">
          <span className="text-[#A3A3A3]">HOME</span>
          <span data-testid="minimap-home-dist" className="text-[#22D3EE]">{homeDist != null ? `${num(homeDist, 0)} m` : "—"}</span>
          <span className="text-[#A3A3A3]">CAP</span>
          <span className="text-[#22D3EE]">{num(drone?.heading, 0)}°</span>
        </div>
      </div>

      {/* Resize handle */}
      <div data-testid="minimap-resize-handle" onMouseDown={startResize}
        className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize"
        style={{ background: "linear-gradient(135deg, transparent 50%, rgba(34,211,238,0.5) 50%)" }} />
    </div>
  );
}
